/**
 * Add Trial Dialog
 * Creates a new trial via backend API (POST /trials)
 * Trial is persisted to S3 with folder structure
 */

import React, {useState} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import {apiService} from '../services/api';

interface AddTrialDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (trialId: string) => void;
  existingTrialIds: string[];
}

const TRIAL_ID_PATTERN = /^[A-Za-z0-9_-]+$/;

const isValidTrialId = (value: string): boolean =>
  value.length > 0 && TRIAL_ID_PATTERN.test(value);

export const AddTrialDialog: React.FC<AddTrialDialogProps> = ({
  open,
  onClose,
  onAdd,
  existingTrialIds,
}) => {
  const [trialId, setTrialId] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const trimmedId = trialId.trim();
  const hasFormatError = trimmedId.length > 0 && !isValidTrialId(trimmedId);

  const handleAdd = async () => {
    // Validate trial ID
    if (!trimmedId) {
      setError('Please enter a trial ID');
      return;
    }

    // Validate trial_id format (alphanumeric with hyphens only)
    if (!isValidTrialId(trimmedId)) {
      setError('Invalid trial ID format. Must contain only letters, numbers, hyphens, and underscores.');
      return;
    }

    // Check if trial already exists locally (case-insensitive, backend stores uppercase)
    if (existingTrialIds.some((id) => id.toUpperCase() === trimmedId.toUpperCase())) {
      setError('This trial ID already exists');
      return;
    }

    // Call backend API to create trial
    setLoading(true);
    setError(null);

    try {
      const result = await apiService.createTrial(
        trialId.trim(),
        description.trim() || undefined
      );

      if (result.success) {
        // Notify parent to refresh trial list
        onAdd(trialId.trim());
        handleClose();
      } else {
        setError(result.message || 'Failed to create trial');
      }
    } catch (err: any) {
      console.error('Error creating trial:', err);
      // Handle API error response
      const errorMessage = err.response?.data?.message
        || err.response?.data?.body?.message
        || err.message
        || 'Failed to create trial. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return; // Don't close while loading
    setTrialId('');
    setDescription('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Trial</DialogTitle>
      <DialogContent>
        <Box sx={{mt: 2}}>
          {error && (
            <Alert severity="error" sx={{mb: 2}}>
              {error}
            </Alert>
          )}

          <TextField
            label="Trial ID"
            fullWidth
            required
            value={trialId}
            onChange={(e) => {
              setTrialId(e.target.value.toUpperCase());
              setError(null);
            }}
            error={hasFormatError}
            placeholder="e.g., A-1426-0303"
            helperText={
              hasFormatError
                ? 'Only letters, numbers, hyphens, and underscores are allowed.'
                : 'Alphanumeric with hyphens/underscores only (auto-uppercased).'
            }
            autoFocus
            disabled={loading}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !loading && !hasFormatError) {
                handleAdd();
              }
            }}
          />

          <TextField
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional: Brief description of the trial"
            helperText="Optional description for reference"
            disabled={loading}
            sx={{mt: 2}}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleAdd}
          variant="contained"
          disabled={!trimmedId || hasFormatError || loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {loading ? 'Creating...' : 'Add Trial'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
